import { Controller } from "@hotwired/stimulus"

/**
 * Court Selector Controller
 *
 * Allows JAP to select which courts to use for a tournament.
 * Courts are loaded dynamically when a club is selected.
 *
 * Usage:
 *   <div data-controller="court-selector"
 *        data-court-selector-url-value="/api/clubs/:club_id/courts">
 *     <select data-court-selector-target="clubSelect" data-action="change->court-selector#loadCourts">
 *       ...clubs...
 *     </select>
 *     <div data-court-selector-target="courtsContainer">
 *       <!-- Courts will be loaded here -->
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["clubSelect", "courtsContainer", "courtCheckboxes", "selectAllCheckbox", "selectedCount"]
  static values = {
    url: String,
    selectedCourts: { type: Array, default: [] }
  }

  connect() {
    // Si un club est déjà sélectionné, charger les terrains
    const clubId = this.getSelectedClubId()
    if (clubId) {
      this.loadCourts()
    }
  }

  getSelectedClubId() {
    if (this.hasClubSelectTarget) {
      return this.clubSelectTarget.value
    }
    // Chercher dans le formulaire parent si pas de target direct
    const clubSelect = this.element.querySelector('select[name*="club_id"]')
    return clubSelect?.value
  }

  async loadCourts() {
    const clubId = this.getSelectedClubId()

    if (!clubId) {
      this.showEmptyState("Sélectionnez un club pour voir les terrains disponibles")
      return
    }

    this.showLoading()

    try {
      const url = `/api/clubs/${clubId}/courts`
      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const courts = await response.json()
      this.renderCourts(courts)
    } catch (error) {
      console.error("Erreur chargement terrains:", error)
      this.showError("Impossible de charger les terrains")
    }
  }

  renderCourts(courts) {
    if (!this.hasCourtsContainerTarget) return

    if (!courts || courts.length === 0) {
      this.showEmptyState("Aucun terrain disponible pour ce club")
      return
    }

    const html = `
      <div class="space-y-3">
        <div class="flex items-center justify-between">
          <label class="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input type="checkbox"
                   data-court-selector-target="selectAllCheckbox"
                   data-action="change->court-selector#toggleAll"
                   class="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500">
            Tous les terrains (${courts.length})
          </label>
          <span class="text-sm text-gray-500" data-court-selector-target="selectedCount">
            0 sélectionné(s)
          </span>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-court-selector-target="courtCheckboxes">
          ${courts.map(court => this.renderCourtCheckbox(court)).join('')}
        </div>

        <p class="text-xs text-gray-500 mt-2">
          Si aucun terrain n'est sélectionné, tous les terrains disponibles du club seront utilisés.
        </p>
      </div>
    `

    this.courtsContainerTarget.innerHTML = html
    this.updateSelectedCount()
  }

  renderCourtCheckbox(court) {
    const isSelected = this.selectedCourtsValue.includes(court.id)
    const statusBadge = this.getStatusBadge(court)

    return `
      <label class="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:border-blue-500 cursor-pointer transition">
        <input type="checkbox"
               name="tournament[court_ids][]"
               value="${court.id}"
               ${isSelected ? 'checked' : ''}
               data-action="change->court-selector#updateSelection"
               class="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500">
        <div class="flex-1 min-w-0">
          <span class="font-medium text-gray-900">${this.escapeHtml(court.nom)}</span>
          <div class="flex items-center gap-2 mt-0.5">
            ${statusBadge}
            ${court.type_localisation ? `<span class="text-xs text-gray-500">${this.escapeHtml(court.type_localisation)}</span>` : ''}
          </div>
        </div>
      </label>
    `
  }

  getStatusBadge(court) {
    if (court.statut === 'disponible') {
      return '<span class="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700">Disponible</span>'
    } else if (court.statut === 'maintenance') {
      return '<span class="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-yellow-100 text-yellow-700">Maintenance</span>'
    } else {
      return '<span class="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-700">Indisponible</span>'
    }
  }

  toggleAll(event) {
    const isChecked = event.target.checked
    const checkboxes = this.courtCheckboxesTarget.querySelectorAll('input[type="checkbox"]')

    checkboxes.forEach(checkbox => {
      checkbox.checked = isChecked
    })

    this.updateSelectedCount()
  }

  updateSelection() {
    this.updateSelectedCount()
    this.updateSelectAllState()
  }

  updateSelectedCount() {
    if (!this.hasSelectedCountTarget) return

    const checkboxes = this.courtCheckboxesTarget?.querySelectorAll('input[type="checkbox"]:checked') || []
    const count = checkboxes.length

    this.selectedCountTarget.textContent = `${count} sélectionné(s)`
  }

  updateSelectAllState() {
    if (!this.hasSelectAllCheckboxTarget || !this.hasCourtCheckboxesTarget) return

    const checkboxes = this.courtCheckboxesTarget.querySelectorAll('input[type="checkbox"]')
    const checkedCount = this.courtCheckboxesTarget.querySelectorAll('input[type="checkbox"]:checked').length

    if (checkedCount === 0) {
      this.selectAllCheckboxTarget.checked = false
      this.selectAllCheckboxTarget.indeterminate = false
    } else if (checkedCount === checkboxes.length) {
      this.selectAllCheckboxTarget.checked = true
      this.selectAllCheckboxTarget.indeterminate = false
    } else {
      this.selectAllCheckboxTarget.checked = false
      this.selectAllCheckboxTarget.indeterminate = true
    }
  }

  showLoading() {
    if (!this.hasCourtsContainerTarget) return

    this.courtsContainerTarget.innerHTML = `
      <div class="flex items-center justify-center py-6">
        <svg class="animate-spin h-5 w-5 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="text-gray-600">Chargement des terrains...</span>
      </div>
    `
  }

  showEmptyState(message) {
    if (!this.hasCourtsContainerTarget) return

    this.courtsContainerTarget.innerHTML = `
      <div class="text-center py-6 text-gray-500">
        <svg class="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
        </svg>
        <p>${this.escapeHtml(message)}</p>
      </div>
    `
  }

  showError(message) {
    if (!this.hasCourtsContainerTarget) return

    this.courtsContainerTarget.innerHTML = `
      <div class="text-center py-6 text-red-500">
        <svg class="mx-auto h-8 w-8 text-red-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
        </svg>
        <p>${this.escapeHtml(message)}</p>
        <button type="button"
                data-action="click->court-selector#loadCourts"
                class="mt-2 text-sm text-blue-600 hover:text-blue-800 underline">
          Réessayer
        </button>
      </div>
    `
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
